import { ColDef, EditableCallbackParams, ValueParserParams } from 'ag-grid-community'
import {
  currencyFormatter,
  numberFormatter,
  percentageFormatter,
} from 'components/AgGrid/utils/formatters'
import type { ApprovalOption } from 'pages/DecisionEditPage/components/ComparisonTabs'
import { ProductDetailsLineItem } from 'ts/interfaces/productsDetails.interface'
import { createDataPointsValue } from '../../DataPoint/createDataPointsValue'
import DataPointIndicator from '../../DataPoint/DataPointIndicator'
import { renderEditableValue } from '../../shared/EditableCell'
import { getTotalDiscountPercentage, validateBdNetValue, ValidationHandle } from '../../validations'

type DataPointsCellProps = {
  rowData?: ProductDetailsLineItem
  showRequestedPrice: boolean
}

const HEADER_CHARACTER_WIDTH_PX = 7
const HEADER_HORIZONTAL_PADDING_PX = 36

const getHeaderMinWidth = (headerName?: string, currentWidth?: number): number => {
  const calculatedHeaderWidth = Math.ceil(
    (headerName?.length ?? 0) * HEADER_CHARACTER_WIDTH_PX + HEADER_HORIZONTAL_PADDING_PX
  )

  return Math.max(calculatedHeaderWidth, Number(currentWidth ?? 0))
}

const DataPointsCell = ({ rowData, showRequestedPrice }: DataPointsCellProps) => {
  if (!rowData || rowData.id === 'totals') {
    return null
  }

  const value = createDataPointsValue({
    baseline: rowData.dataPoints,
    ogmOrMargin: rowData.requestNetMarginPercentage,
    requestedValue: rowData.requestNetMarginPercentage,
    isRequestedPriceActive: showRequestedPrice,
  })

  if (!value) {
    return null
  }

  return <DataPointIndicator value={value} />
}

const withMinWidth = <T,>(col: ColDef<T>): ColDef<T> => ({
  ...col,
  minWidth: getHeaderMinWidth(col.headerName, col.width),
  wrapHeaderText: true,
  autoHeaderHeight: true,
})

const baseProductsDetailsColDefs: ColDef<ProductDetailsLineItem>[] = [
  {
    headerName: 'Line Type',
    field: 'lineTypeCode',
    width: 90,
  },
  {
    headerName: 'Product #',
    field: 'productGenericNumberText',
    width: 120,
  },
  {
    headerName: 'Product Description',
    field: 'productGenericDescriptionText',
    width: 300,
  },
  {
    headerName: 'PL',
    field: 'assosiatePlCode',
    width: 50,
  },
  {
    headerName: 'Qty',
    field: 'estimatedQuantity',
    width: 86,
    valueFormatter: numberFormatter,
    type: 'numericColumn',
    headerClass: 'ag-right-aligned-header',
  },
  {
    headerName: 'Data Points',
    field: 'dataPoints',
    width: 230,
  },
  {
    headerName: 'Total Discount %',
    field: 'totalDiscountRequestPercentage',
    width: 130,
    valueGetter: (params) =>
      params.node?.rowPinned
        ? null
        : params.data?.totalDiscountAuthPercentageUpdated ||
          params.data?.totalDiscountRequestPercentageUpdated ||
          0,
    valueFormatter: (params) =>
      params.node?.rowPinned
        ? ''
        : getTotalDiscountPercentage(
            params.data ?? { highResellerAStandardDiscountPercentage: 0 },
            params.value ?? 0
          ),
    type: 'numericColumn',
    headerClass: 'ag-right-aligned-header',
  },
  {
    headerName: 'BD Net',
    field: 'extendedRequestBdNet',
    width: 90,
    valueGetter: (params) =>
      params.node?.rowPinned
        ? ((params.data as { bdNet?: number } | undefined)?.bdNet ?? 0)
        : params.data?.authBdnetPriceAmountUpdated ||
          params.data?.requestBdNetPriceAmountUpdated ||
          0,
    valueFormatter: currencyFormatter,
    type: 'numericColumn',
    editable: (params: EditableCallbackParams<ProductDetailsLineItem>) =>
      !params.node.rowPinned && !params.context?.isUSDSelected,
    cellEditor: 'agNumberCellEditor',
    valueParser: (params: ValueParserParams) => Number(params.newValue),
    valueSetter: (params) => validateBdNetValue(params),
    cellRenderer: renderEditableValue,
    cellClass: (params) =>
      params.node.rowPinned || params.context?.isUSDSelected ? '' : 'editable-bordered-cell',
  },
  {
    headerName: 'OGM %',
    field: 'requestNetMarginPercentage',
    width: 75,
    valueGetter: (params) =>
      params.node?.rowPinned
        ? null
        : (params.data?.authNetMarginPercentageUpdated ??
          params.data?.requestNetMarginPercentageUpdated ??
          0),
    valueFormatter: percentageFormatter,
    type: 'numericColumn',
    editable: (params: EditableCallbackParams<ProductDetailsLineItem>) =>
      !params.node.rowPinned && !params.context?.isUSDSelected,
    cellEditor: 'agNumberCellEditor',
    valueParser: (params: ValueParserParams) => Number(params.newValue),
    valueSetter: (params) => ValidationHandle(params),
    cellRenderer: renderEditableValue,
    cellClass: (params) =>
      params.node.rowPinned || params.context?.isUSDSelected ? '' : 'editable-bordered-cell',
  },
  {
    headerName: "Expert Add'l%",
    field: 'calcGuidanceExpertPercentage',
    width: 75,
    valueFormatter: percentageFormatter,
    type: 'numericColumn',
    hide: true,
  },
  {
    headerName: "Typical Add'l%",
    field: 'calcGuidanceTypicalPercentage',
    width: 75,
    valueFormatter: percentageFormatter,
    type: 'numericColumn',
    hide: true,
  },
  {
    headerName: "Floor Add'l%",
    field: 'calcGuidanceFloorPercentage',
    width: 90,
    valueFormatter: percentageFormatter,
    type: 'numericColumn',
    hide: true,
  },
  {
    headerName: 'Historical Top 25% discount',
    field: 'historicalTop25DiscountPercentage',
    width: 90,
    valueFormatter: percentageFormatter,
    type: 'numericColumn',
    hide: true,
  },
  {
    headerName: 'Historical Median discount',
    field: 'historicalMedianDiscountPercentage',
    width: 90,
    valueFormatter: percentageFormatter,
    type: 'numericColumn',
    hide: true,
  },
  {
    headerName: 'Historical Bottom 25% discount',
    field: 'historicalBottom25DiscountPercentage',
    width: 90,
    valueFormatter: percentageFormatter,
    type: 'numericColumn',
    hide: true,
  },
  {
    colId: 'requested-total-discount-percent',
    headerName: 'Total Discount %',
    field: 'totalDiscountRequestPercentage',
    width: 130,
    valueGetter: (params) =>
      params.node?.rowPinned
        ? null
        : (params.data?.totalDiscountAuthPercentage ??
          params.data?.totalDiscountRequestPercentageDb ??
          0),
    valueFormatter: (params) =>
      params.node?.rowPinned
        ? ''
        : getTotalDiscountPercentage(
            params.data ?? { highResellerAStandardDiscountPercentage: 0 },
            Number(params.value ?? 0)
          ),
    type: 'numericColumn',
    hide: true,
  },
  {
    colId: 'requested-bd-net',
    headerName: 'BD Net',
    field: 'extendedRequestBdNet',
    width: 90,
    valueGetter: (params) =>
      params.node?.rowPinned
        ? null
        : (params.data?.authBdnetPriceAmount ?? params.data?.requestBdNetPriceAmount ?? 0),
    valueFormatter: currencyFormatter,
    type: 'numericColumn',
    hide: true,
  },
  {
    colId: 'requested-ogm-percent',
    headerName: 'OGM %',
    field: 'requestNetMarginPercentage',
    width: 75,
    valueGetter: (params) =>
      params.node?.rowPinned
        ? null
        : (params.data?.authNetMarginPercentage ?? params.data?.requestNetMarginPercentageDb ?? 0),
    valueFormatter: percentageFormatter,
    type: 'numericColumn',
    hide: true,
  },
]

export const productsDetailsColDefs: ColDef<ProductDetailsLineItem>[] =
  baseProductsDetailsColDefs.map(withMinWidth)

const buildApprovalColDefs = (approvalOption: ApprovalOption): ColDef<ProductDetailsLineItem>[] => {
  const { id } = approvalOption
  return [
    withMinWidth({
      colId: `approval-${id}-total-discount-percent`,
      headerName: `Total Discount %`,
      width: 130,
      valueGetter: (params) =>
        params.node?.rowPinned
          ? null
          : (params.data?.approvalData?.[id]?.totalDiscountPercentage ?? null),
      valueFormatter: percentageFormatter,
      type: 'numericColumn',
      hide: true,
    }),
    withMinWidth({
      colId: `approval-${id}-bd-net`,
      headerName: `BD Net`,
      width: 90,
      valueGetter: (params) =>
        params.node?.rowPinned ? null : (params.data?.approvalData?.[id]?.bdNet ?? null),
      valueFormatter: currencyFormatter,
      type: 'numericColumn',
      hide: true,
    }),
    withMinWidth({
      colId: `approval-${id}-ogm-percent`,
      headerName: `OGM %`,
      width: 75,
      valueGetter: (params) =>
        params.node?.rowPinned ? null : (params.data?.approvalData?.[id]?.ogmPercent ?? null),
      valueFormatter: percentageFormatter,
      type: 'numericColumn',
      hide: true,
    }),
  ]
}

export const getProductsDetailsColDefs = (
  showRequestedPrice: boolean,
  approvalOptions: ApprovalOption[] = []
): ColDef<ProductDetailsLineItem>[] => {
  const approvalColDefs = approvalOptions.flatMap(buildApprovalColDefs)
  const allCols = [...productsDetailsColDefs, ...approvalColDefs]

  return allCols.map((col) => {
    if (col.field === 'dataPoints') {
      return {
        ...col,
        cellRenderer: (params: { data?: ProductDetailsLineItem }) => (
          <DataPointsCell rowData={params.data} showRequestedPrice={showRequestedPrice} />
        ),
      }
    }

    if ((col.colId || '').startsWith('requested-')) {
      return { ...col, hide: !showRequestedPrice }
    }

    return col
  })
}
